import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ShieldCheck, ArrowLeft, Home as HomeIcon, User, Video, Volume2, FileText, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import PaymentSecurity from "../components/PaymentSecurity";
import { Seo, serviceJsonLd } from "../components/Seo";

export default function Security() {
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/digital-course").then((r) => setCourse(r.data)).catch(() => setCourse(null)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Säker på nätet – kurs om trygghet på internet"
        description="Lär dig känna dig trygg på internet: bluff-sms, säkra lösenord, virusskydd och BankID. Välj mellan personligt hembesök eller en digital kurs med video och PDF."
        path="/sakerhet"
        jsonLd={serviceJsonLd({ name: "Säker på nätet", description: "Kurs om internetsäkerhet för seniorer – hembesök eller digital kurs.", path: "/sakerhet" })}
      />
      <Link to="/" data-testid="security-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <ShieldCheck size={46} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Säker på nätet
      </h1>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Vår kanske viktigaste kurs.
      </p>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Vi hjälper dig att känna dig trygg på internet – med bluff-sms, säkra lösenord, virusskydd och BankID.
      </p>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Välj det format som passar dig bäst.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-10">
        {/* VAL 1 */}
        <div data-testid="option-onsite" className="bg-white border-2 border-gray-300 rounded-xl p-8 flex flex-col">
          <span className="inline-flex items-center gap-2 bg-blue-50 text-[#0056D2] rounded-full px-4 py-1 text-base font-bold self-start">VAL 1</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-4 flex items-center gap-3">
            <User size={30} strokeWidth={2.5} className="text-[#0056D2]" /> Kurs på plats
          </h2>
          <p className="text-lg text-[#1A1A1A] mt-3">
            Ett personligt hembesök där vi går igenom säkerheten direkt på dina egna apparater –
            telefon, surfplatta och dator.
          </p>
          <ul className="mt-4 space-y-3 flex-1">
            {["Vi kollar ditt virusskydd", "Vi pratar om bluff-sms", "Säkra lösenord som är lätta att komma ihåg", "Så använder du BankID tryggt"].map((f) => (
              <li key={f} className="flex items-start gap-3 text-lg text-[#1A1A1A]">
                <CheckCircle2 className="text-[#0B7A3F] shrink-0 mt-1" size={22} strokeWidth={2.5} /> {f}
              </li>
            ))}
          </ul>
          <Link to="/sakerhet/hembesok" data-testid="book-onsite"
            className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-6 mt-6 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors">
            Boka hembesök <ArrowRight size={24} strokeWidth={2.5} />
          </Link>
        </div>

        {/* VAL 2 */}
        <div data-testid="option-digital" className="bg-white border-2 border-gray-300 rounded-xl p-8 flex flex-col">
          <span className="inline-flex items-center gap-2 bg-blue-50 text-[#0056D2] rounded-full px-4 py-1 text-base font-bold self-start">VAL 2</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-4 flex items-center gap-3">
            <Video size={30} strokeWidth={2.5} className="text-[#0056D2]" /> Digital kurs
          </h2>
          <p className="text-lg text-[#1A1A1A] mt-3">
            Ett helt digitalt paket som du köper och får tillgång till direkt – titta hemma i lugn och ro,
            om och om igen.
          </p>
          <ul className="mt-4 space-y-3 flex-1">
            <li className="flex items-start gap-3 text-lg text-[#1A1A1A]"><Video className="text-[#0056D2] shrink-0 mt-1" size={22} strokeWidth={2.5} /> Pedagogiska videofilmer i korta kapitel</li>
            <li className="flex items-start gap-3 text-lg text-[#1A1A1A]"><Volume2 className="text-[#0056D2] shrink-0 mt-1" size={22} strokeWidth={2.5} /> Mycket tydligt och klart tal (ljudspår)</li>
            <li className="flex items-start gap-3 text-lg text-[#1A1A1A]"><FileText className="text-[#0056D2] shrink-0 mt-1" size={22} strokeWidth={2.5} /> Medföljande PDF-guide att skriva ut</li>
          </ul>

          {loading ? (
            <div className="mt-6 flex items-center gap-2 text-[#4A4A4A]"><Loader2 className="animate-spin" size={22} /> Laddar…</div>
          ) : course ? (
            <>
              <p className="font-heading text-3xl font-extrabold text-[#1A1A1A] mt-6">{Math.round(course.price)} kr <span className="text-base font-medium text-[#4A4A4A]">inkl. moms</span></p>
              <button data-testid="buy-digital" onClick={() => navigate(`/kassa/${course.id}`)}
                className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-6 mt-4 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors">
                Köp digital kurs <ArrowRight size={24} strokeWidth={2.5} />
              </button>
              <p className="text-base text-[#4A4A4A] mt-3 text-center">Du anger bara din e-post och betalar med kort. Ingen inloggning behövs. Vi förbereder ditt material och skickar din personliga länk till din e-post.</p>
              <PaymentSecurity align="center" className="mt-4" />
            </>
          ) : (
            <p className="text-lg text-[#4A4A4A] mt-6">Den digitala kursen är inte tillgänglig just nu.</p>
          )}
        </div>
      </div>

      <div className="text-center mt-10">
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
          <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
        </Link>
      </div>
    </div>
  );
}
